<template>
  <div class="history-list">
    <!-- Loading State (initial load) -->
    <div v-if="loading && games.length === 0" class="list-loading">
      <span class="material-symbols-outlined spinning">sync</span>
      <span>Loading game history...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="games.length === 0" class="list-empty">
      <span class="material-symbols-outlined empty-icon">history</span>
      <p class="empty-title">No game history</p>
      <p class="empty-hint">Completed games will appear here</p>
    </div>

    <!-- History List -->
    <div v-else class="history-content">
      <div class="games-list">
        <div
          v-for="game in games"
          :key="game.id"
          class="history-row"
          @click="$emit('view', game)"
        >
          <div class="row-main">
            <div class="game-info">
              <span class="room-code">{{ game.room_code }}</span>
              <span class="game-date">{{ formatDate(game.finished_at || game.updated_at) }}</span>
            </div>

            <div class="game-result">
              <span class="final-score">
                {{ game.team_a_score.toLocaleString() }} - {{ game.team_b_score.toLocaleString() }}
              </span>
              <span v-if="game.winning_team" class="winner-badge">
                {{ game.winning_team === 'A' ? game.team_a_name : game.team_b_name }} won
              </span>
              <span v-else class="tie-badge">Tie</span>
            </div>
          </div>

          <div class="row-meta">
            <span class="days-ago">
              {{ game.days_old === 0 ? 'Today' : game.days_old === 1 ? 'Yesterday' : `${game.days_old} days ago` }}
            </span>
            <span class="view-details">
              View details
              <span class="material-symbols-outlined">chevron_right</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="hasMore" class="load-more-container">
        <button
          class="load-more-btn"
          :disabled="loading"
          @click="$emit('loadMore')"
        >
          <span v-if="loading" class="material-symbols-outlined spinning">sync</span>
          <span v-else>Load More</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GameSummary } from '@/types/game'

defineProps<{
  games: GameSummary[]
  loading: boolean
  hasMore: boolean
}>()

defineEmits<{
  (e: 'view', game: GameSummary): void
  (e: 'loadMore'): void
}>()

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}
</script>

<style scoped>
.history-list {
  padding: 1rem 0;
}

/* Loading State */
.list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.list-loading .spinning {
  font-size: 2rem;
  color: #e8c547;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Empty State */
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: rgba(232, 197, 71, 0.4);
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 0.5rem 0;
}

.empty-hint {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

/* Games List */
.games-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* History Row */
.history-row {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.history-row:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(232, 197, 71, 0.2);
}

.row-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
  min-width: 0;
}

.game-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex-shrink: 0;
}

.room-code {
  font-size: 1rem;
  font-weight: 700;
  color: #e8c547;
  letter-spacing: 0.1em;
}

.game-date {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.game-result {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 0;
  flex-shrink: 1;
}

.final-score {
  font-size: 1.125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}

.winner-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: #4ade80;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tie-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.row-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  gap: 1rem;
}

.days-ago {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}

.view-details {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: rgba(232, 197, 71, 0.7);
  transition: color 0.2s ease;
}

.history-row:hover .view-details {
  color: #e8c547;
}

.view-details .material-symbols-outlined {
  font-size: 1rem;
  transition: transform 0.2s ease;
}

.history-row:hover .view-details .material-symbols-outlined {
  transform: translateX(2px);
}

/* Load More */
.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.load-more-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.load-more-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(232, 197, 71, 0.3);
}

.load-more-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.load-more-btn .spinning {
  font-size: 1rem;
  animation: spin 1s linear infinite;
}
</style>
